import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from './ui/switch';

// GET/PATCH /api/users/me/night-recap — the 07:00 job runs in the backend.
export function NightRecapSettings() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [pref, setPref] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const telegramLinked = pref?.telegram_linked ?? Boolean(user?.telegram_chat_id || user?.telegram_connected);

  useEffect(() => {
    setFailed(false);
    api.get('/users/me/night-recap')
      .then((r) => setPref(r.data))
      .catch((e) => ([404, 501].includes(e.response?.status) ? setUnavailable(true) : setFailed(true)));
  }, [reload]);

  const save = async (patch) => {
    const prev = pref;
    setPref({ ...pref, ...patch });
    setSaving(true);
    try {
      const r = await api.patch('/users/me/night-recap', patch);
      setPref(r.data);
      toast.success(t('recap_saved'));
    } catch (e) {
      setPref(prev);
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : t('recap_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  if (unavailable) {
    return <span className="text-xs text-ink-muted" data-testid="recap-unavailable">{t('recap_unavailable')}</span>;
  }
  if (failed) {
    return (
      <button type="button" onClick={() => setReload((n) => n + 1)} data-testid="recap-retry" className="text-xs text-terracotta-700 underline">
        {t('recap_load_failed')}
      </button>
    );
  }
  if (!pref) return <span className="text-xs text-ink-muted">{t('loading')}</span>;

  const disabled = saving || !pref.enabled;
  return (
    <div className="flex flex-col items-end gap-3" data-testid="recap-settings">
      <label className="flex items-center gap-2 text-sm text-ink">
        {pref.enabled ? t('recap_on') : t('recap_off')}
        <Switch checked={pref.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="recap-enabled-switch" />
      </label>
      <div className="flex items-center gap-4 text-xs">
        <label className={`flex items-center gap-2 ${disabled ? 'opacity-50' : ''}`}>
          <Switch checked={pref.email} disabled={disabled} onCheckedChange={(v) => save({ email: v })} data-testid="recap-email-switch" />
          {t('recap_email')}
        </label>
        <label className={`flex items-center gap-2 ${disabled || !telegramLinked ? 'opacity-70' : ''}`} title={!telegramLinked ? t('recap_telegram_link_first') : undefined}>
          <Switch checked={pref.telegram && telegramLinked} disabled={disabled || !telegramLinked} onCheckedChange={(v) => save({ telegram: v })} data-testid="recap-telegram-switch" />
          Telegram
        </label>
      </div>
      {!telegramLinked && <span className="text-[11px] text-ink-muted" data-testid="recap-telegram-hint">{t('recap_telegram_link_first')}</span>}
      {pref.enabled && !pref.email && !(pref.telegram && telegramLinked) && (
        <span className="text-[11px] text-[#8a6224]" data-testid="recap-no-channel">{t('recap_no_channel')}</span>
      )}
      {pref.last_sent_at && (
        <span className="text-[11px] text-ink-muted" data-testid="recap-last-sent">
          {t('recap_last_sent', { at: new Date(pref.last_sent_at).toLocaleString() })}
        </span>
      )}
    </div>
  );
}
